import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  Link,
  Paper,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CocoModal from 'components/coco-modal/coco-modal';
import EmailIcon from '@mui/icons-material/Email';
import React, { useState } from 'react';
import { msg, Trans } from '@lingui/macro';
import MutliEmailInput, {
  sendInvites,
} from 'components/multi-email-input/MultiEmailInput';
import { LoadingButton } from '@mui/lab';
import { useCommunityDetails } from 'atoms/community-details';
import KeyIcon from '@mui/icons-material/Key';
import InfoIcon from '@mui/icons-material/Info';
import C from './add-members-login-modal.css';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import LinkIcon from '@mui/icons-material/Link';
import {
  CheckCircle,
  ExpandLess,
  ExpandMore,
  Key,
  OpenInNew,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import ControlledTextField from 'components/forms/ControlledTextField';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import { LoginAllowance } from '@cocoplatform/coco-rtc-shared';
import { qualifiedUserName } from 'utils/user';
import { reportServerError } from 'utils/report-error';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import csvTemplate from '../../assets/students_template.csv';
import useFileSelector from 'utils/hooks/useFileSelector';
import { ScrollableContainer } from 'components/scrollable-container/ScrollableContainer';
import cc from 'classnames';
import ClassroomIcon from './google-classroom.png';
import { useRouteMatch } from 'react-router-dom';
import ROUTES from 'constants/routes';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export function ShareLinkPage({
  communityId,
  showGoogleClassroom,
  sendClassroomInvites,
  invitesSent,
  isEmailInvite,
}: {
  communityId: string;
  showGoogleClassroom?: boolean;
  sendClassroomInvites?: () => void;
  invitesSent?: number | null;
  isEmailInvite?: boolean;
}) {
  const { community } = useCommunityDetails({
    communityId,
  });

  const code = community?.genericInvitationCode?.code;
  const displayURL =
    `${location.origin}/#${code}${showGoogleClassroom ? '/classroom' : ''}`.replace(
      /^https?:\/\//,
      '',
    );
  const [didCopyLink, setCopyLink] = useState(false);

  const [sendingInvites, setSendingInvites] = useState(false);

  const routeMatch = useRouteMatch();
  const isEditor = window?.location.pathname.includes('editor');

  return (
    <Stack p={3}>
      <Typography>
        {isEmailInvite ? (
          <Trans>
            An email invite with the link to sign in has been sent.
            <br />
            <br />
            Alternatively, you can directly share the community link for members
            to sign in. The link is also available on the Members page.
          </Trans>
        ) : (
          <Trans>
            Share the community link with students, so they can sign in with
            their{' '}
            {showGoogleClassroom ? 'Google accounts' : 'username and password'}.
            The link is also available on the Members page.
          </Trans>
        )}
      </Typography>
      <Stack
        justifyContent='space-between'
        direction='row'
        p={2}
        mt={2}
        sx={{
          backgroundColor: 'var(--color-base)',
        }}
        alignItems='center'
      >
        <Typography
          fontWeight='bold'
          sx={{
            color: 'var(--color-primary)',
            textDecoration: 'underline',
          }}
        >
          {displayURL}
        </Typography>
        <Box></Box>
        <Button
          onClick={() => {
            navigator.clipboard.writeText(displayURL);
            setCopyLink(true);

            setTimeout(() => {
              setCopyLink(false);
            }, 1000);
          }}
          variant='contained'
          startIcon={
            didCopyLink ? (
              <DoneIcon
                style={{
                  width: '1rem',
                }}
              />
            ) : (
              <ContentCopyIcon
                style={{
                  width: '1rem',
                }}
              />
            )
          }
        >
          {didCopyLink ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
        </Button>
      </Stack>
      {isEditor && (
        <>
          <br />
          <Stack gap={1} direction='row' alignItems='center'>
            <Typography
              sx={{
                color: 'var(--color-subtext)',
              }}
            >
              <Trans>
                You can view invited members anytime on the Members page.{' '}
              </Trans>
            </Typography>
            <Link
              sx={{
                color: 'var(--color-primary)',
                textDecoration: 'underline !important',
              }}
              target='_blank'
              href={`/app/dashboard/${communityId}/members`}
            >
              <OpenInNew />
            </Link>
          </Stack>
        </>
      )}
      {showGoogleClassroom && (
        <>
          <Divider>
            <Typography>
              <Trans>OR</Trans>
            </Typography>
          </Divider>
          <Box mt={2} />
          <Button
            color='secondary'
            variant='contained'
            size='large'
            disableElevation
            target='_blank'
            startIcon={<img src={ClassroomIcon} style={{ height: '1.5rem' }} />}
            href={`https://classroom.google.com/share?url=${encodeURIComponent(displayURL)}&body=${encodeURIComponent('Click the link below and sign in with your Google account to join our class community on CoCo.')}&itemtype=announcement`}
            onClick={() => {
              window.open(
                `https://classroom.google.com/share?url=${encodeURIComponent(displayURL)}&body=${encodeURIComponent('Click the link below and sign in with your Google account to join our class community on CoCo.')}&itemtype=announcement`,
                'MyWindow',
                'width=600,height=300',
              );
            }}
          >
            <Trans>Share to Google Classroom</Trans>
          </Button>
          <Box my={2} />
          <Typography>
            <Trans>
              If students can receive external emails, you can also click the
              button below to send invites to all students via email.
            </Trans>
          </Typography>
          <Box my={2} />
          <LoadingButton
            onClick={async () => {
              setSendingInvites(true);
              await sendClassroomInvites?.();
              setSendingInvites(false);
            }}
            startIcon={
              invitesSent !== null &&
              invitesSent !== undefined && (
                <CheckCircle style={{ width: '1.5rem' }} />
              )
            }
            variant='contained'
            color='primary'
            disabled={invitesSent !== null && invitesSent !== undefined}
            loading={sendingInvites}
          >
            {invitesSent === null || invitesSent === undefined ? (
              <Trans>Send invites by email</Trans>
            ) : (
              <Trans>
                {invitesSent} invite{invitesSent > 1 ? 's' : ''} sent
              </Trans>
            )}
          </LoadingButton>
        </>
      )}
    </Stack>
  );
}

export function DownloadCSVButton({ communityId }: { communityId: string }) {
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='flex-end'
      sx={{
        color: 'var(--color-primary)',
        cursor: 'pointer',
      }}
      onClick={async () => {
        const resp = await httpClient.get(
          `/community/${communityId}/login-allowances.csv`,
          {
            responseType: 'text',
          },
        );
        const link = document.createElement('a');
        link.download = 'coco-students.csv';
        link.href = URL.createObjectURL(
          new Blob([resp.data], { type: 'application/csv' }),
        );
        link.click();
      }}
    >
      <InfoIcon
        onMouseOver={(e) => {
          setDownloadAnchorEl(e.currentTarget);
        }}
        onMouseOut={() => {
          setDownloadAnchorEl(null);
        }}
        sx={{
          width: '1.2rem',
          cursor: 'pointer',
        }}
      />
      <Popover
        id='mouse-over-popover'
        sx={{ pointerEvents: 'none' }}
        open={Boolean(downloadAnchorEl)}
        anchorEl={downloadAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={() => {
          setDownloadAnchorEl(null);
        }}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1 }}>
          <Trans>
            The system does not store passwords in plain text
            <br /> for security.
          </Trans>
        </Typography>
      </Popover>
      <Typography variant='caption'>
        <Trans>Download CSV</Trans>
      </Typography>
    </Stack>
  );
}

function ManualAddTab({
  communityId,
  onSuccess,
}: {
  communityId: string;
  onSuccess: (data: LoginAllowance[]) => void;
}) {
  const { _ } = useLingui();
  const [submittingUsername, setSubmittingUsername] = useState(false);

  const [memberAddCount, setMemberAddCount] = useState(0);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const { register, control, handleSubmit, reset } = useForm();

  const { patchCommunityDetails } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });

  const { triggerRef } = useFileSelector({
    onSelect: handleFileSelect,
  });

  const [expandInstructions, setExpandInstructions] = useState(false);

  async function handleFileSelect(csvFile: File) {
    const formData = new FormData();
    formData.append('users', JSON.stringify({ users: [] }));
    formData.append('csv', csvFile);

    const { data: res } = await httpClient.post(
      `/community/${communityId}/login-allowances`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    const loginAllowances: LoginAllowance[] = res.loginAllowances;
    await onSuccess(loginAllowances);
  }

  return (
    <Grid alignItems='center' mt={2} spacing={1} container>
      <Grid item xs={9}>
        <Typography variant='body1'>
          <Trans>Create credentials for each student:</Trans>
        </Typography>
      </Grid>
      <Grid item xs={12} mt={1} />
      <Grid item xs>
        <ControlledTextField
          className={cc(C.input, C.groupInput)}
          autoFocus
          name='username'
          variant='outlined'
          placeholder={_(msg`Enter Username`)}
          autoComplete='new-username'
          size='small'
          control={control}
        />
      </Grid>
      <Grid item xs>
        <ControlledTextField
          className={cc(C.input, C.groupInput)}
          name='password'
          variant='outlined'
          placeholder={_(msg`Enter Password`)}
          autoComplete='new-password'
          type='password'
          size='small'
          control={control}
        />
      </Grid>
      <Grid item xs>
        <ControlledTextField
          className={cc(C.input, C.groupInput)}
          name='groups'
          variant='outlined'
          placeholder={_(msg`Enter Group (Optional)`)}
          autoComplete='new-group'
          size='small'
          control={control}
        />
      </Grid>
      <Grid item xs={2}>
        <LoadingButton
          variant='contained'
          onClick={handleSubmit(async (data) => {
            setSubmittingUsername(true);
            const formData = new FormData();
            formData.append(
              'users',
              JSON.stringify({
                users: [
                  {
                    username: data.username,
                    password: data.password,
                    groups: data.groups?.split(',').map((g) => g.trim()),
                  },
                ],
              }),
            );

            const { data: res } = await httpClient.post(
              `/community/${communityId}/login-allowances`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              },
            );
            const loginAllowances: LoginAllowance[] = res.loginAllowances;
            await onSuccess(loginAllowances);
            setSubmittingUsername(false);
            reset();
          })}
          loading={submittingUsername}
          fullWidth
          style={{ minWidth: 0, height: '35px' }}
        >
          Add
        </LoadingButton>
      </Grid>
      <Grid item xs={12} my={1}>
        <Divider>
          <Typography fontWeight='bold'>
            <Trans>OR</Trans>
          </Typography>
        </Divider>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='body1'>
          <Trans>
            Upload a CSV file to add entries for multiple students at once.
          </Trans>
        </Typography>
      </Grid>
      <Grid item xs={12} mt={1} />
      <Grid item xs={6}>
        <Button
          href={csvTemplate}
          download='coco_username_template.csv'
          fullWidth
          className={C.filledOutlineBtn}
          variant='outlined'
        >
          <Typography fontWeight='bold'>
            <Trans>STEP 1:</Trans>
          </Typography>
          &nbsp;
          <Typography>
            <Trans>Download Template</Trans>
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button
          ref={triggerRef}
          fullWidth
          className={C.filledOutlineBtn}
          variant='outlined'
        >
          <Typography fontWeight='bold'>
            <Trans>STEP 2:</Trans>
          </Typography>
          &nbsp;
          <Typography>
            <Trans>Upload CSV</Trans>
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={12} mt={1} />
      <Grid item xs={12}>
        <Link variant='body1'>
          <Stack
            direction='row'
            alignItems='center'
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setExpandInstructions(!expandInstructions);
            }}
          >
            <Trans>Instructions for using the template:</Trans>
            <Stack justifyContent='center' alignItems='center'>
              {expandInstructions ? <ExpandLess /> : <ExpandMore />}
            </Stack>
          </Stack>
        </Link>
        <Collapse in={expandInstructions}>
          <Typography variant='subtitle2' component='div'>
            <ol>
              <li>
                <Trans>
                  Open <code>students_template.csv</code> in Excel or Google
                  Sheets.
                </Trans>
              </li>
              <li>
                <Trans>
                  Leave the first row (username, password, group) untouched.
                </Trans>
              </li>
              <li>
                <Trans>
                  Replace placeholders in subsequent rows with each
                  student&#39;s details. The "group" column is optional.
                </Trans>
              </li>
              <li>
                <Trans>
                  Save the file: in Excel, select{' '}
                  <strong>File &gt; Save</strong>; in Google Sheets, select{' '}
                  <strong>File &gt; Download as &gt; CSV</strong>.
                </Trans>
              </li>
              <li>
                <Trans>
                  Click <strong>Upload CSV</strong> and select your file to add
                  students to your community.
                </Trans>
              </li>
            </ol>
            <Trans>
              <strong>Note</strong>: Do not rearrange the columns, as this will
              prevent processing. If no password is provided, a default password{' '}
              <code>&lt;username&gt;&lt;dd&gt;</code> (where{' '}
              <code>&lt;dd&gt;</code> is today&#39;s date) will be created.
            </Trans>
          </Typography>
        </Collapse>
      </Grid>
    </Grid>
  );
}

function AutoAddTab({
  communityId,
  onSuccess,
}: {
  communityId: string;
  onSuccess: (data: LoginAllowance[]) => void;
}) {
  const { _ } = useLingui();
  const [generatingAuto, setGeneratingAuto] = useState(false);
  const [memberAddCount, setMemberAddCount] = useState(0);

  const [downloadAnchorEl, setDownloadAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const { register, control, handleSubmit, reset } = useForm();

  return (
    <Grid alignItems='center' mt={2} spacing={1} container>
      <Grid item xs={9}>
        <Typography variant='body1'>
          <Trans>Auto generate a list of unique usernames for students:</Trans>
        </Typography>
      </Grid>
      <Grid item xs={12} mt={1} />
      <Grid item xs={5}>
        <ControlledTextField
          className={cc(C.input, C.groupInput)}
          autoFocus
          name='count'
          type='number'
          variant='outlined'
          placeholder={_(msg`Enter how many to generate (20, 30...)`)}
          autoComplete='new-username'
          size='small'
          control={control}
        />
      </Grid>
      <Grid item xs={4}>
        <ControlledTextField
          className={cc(C.input, C.groupInput)}
          name='groups'
          variant='outlined'
          placeholder={_(msg`Enter Group (Optional)`)}
          autoComplete='new-group'
          size='small'
          control={control}
        />
      </Grid>
      <Grid item xs={3}>
        <LoadingButton
          variant='contained'
          loading={generatingAuto}
          onClick={handleSubmit(async (data) => {
            setGeneratingAuto(true);
            const { data: res } = await httpClient.post(
              `/community/${communityId}/login-allowances/bulk`,
              {
                count: Number(data.count),
                groupName: data.groups,
              },
            );
            const loginAllowances: LoginAllowance[] = res.loginAllowances;
            await onSuccess(loginAllowances);
            setGeneratingAuto(false);
            reset();
          })}
          fullWidth
          style={{ minWidth: 0, height: '35px' }}
        >
          Generate
        </LoadingButton>
      </Grid>
      <Grid item xs={12} my={1} />
      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: 'var(--color-base)',
          }}
          p={2}
        >
          <Typography variant='body2' textAlign='center'>
            <Trans>
              <span
                style={{
                  color: 'var(--color-primary)',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                }}
              >
                Password format:
              </span>{' '}
              <span
                style={{
                  fontStyle: 'italic',
                }}
              >
                username + today's date
              </span>{' '}
              (e.g., if generated username is <strong>redpanda37</strong> and
              the date is Jan <strong>23</strong>, the password would be{' '}
              <strong>redpanda3723</strong>)
            </Trans>
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function AddMembersLoginModal({
  onClose,
  communityId,
  onSubmit,
  onCancel,
}: {
  onClose: () => void;
  onCancel?: () => void;
  communityId: string;
  onSubmit?: () => void;
}) {
  const { _ } = useLingui();
  const {
    loadCommunityDetails,
    patchCommunityDetails,
    removeMember,
    community,
    updateGroup,
    createGroup,
  } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });
  const [activeTab, setActiveTab] = useState(0);
  const [addedMembers, setAddedMembers] = useState<LoginAllowance[]>([]);
  const [shareLinkVisible, setShareLinkVisible] = useState(false);

  function locallyUpdateLoginAllowances(loginAllowances: LoginAllowance[]) {
    patchCommunityDetails((c) => ({
      ...c,
      coCreatorCount: (c.coCreatorCount ?? 0) + loginAllowances.length,
      members: (c.members ?? []).concat(
        loginAllowances.map((la) => ({
          isSelf: false,
          isApproved: true,
          isAccepted: true,
          isReported: false,
          isBlocked: false,
          roles: ['MEMBER'],
          pendingRoles: [],
          user: {
            ...la.user,
            loginAllowanceId: la.id,
          },
        })),
      ),
    }));

    // Re-trigger community details refresh, just to get groups updated
    loadCommunityDetails();

    onSubmit?.();
  }

  async function bulkDeleteUsers({ userIds }: { userIds: string[] }) {
    if (userIds.length === 0) return;

    const usersToDelete = userIds.map((userId) => {
      return { userId };
    });

    try {
      await httpClient.post(`/community/${communityId}/bulk-delete-users`, {
        users: usersToDelete,
      });
    } catch (error) {
      reportServerError({
        error,
        title: _(msg`Failed to delete users`),
      });
      return;
    }

    removeMember(usersToDelete);
  }

  return (
    <CocoModal
      onClose={onClose}
      header={
        <Stack direction='row'>
          <Typography color='white'>
            {shareLinkVisible ? (
              <Stack direction='row' alignItems='center'>
                <LinkIcon style={{ width: '2rem' }} />
                <Trans>Share Community Link</Trans>
              </Stack>
            ) : (
              <Stack direction='row' alignItems='center'>
                <KeyIcon style={{ width: '2rem' }} />
                <Trans>Create Student Logins</Trans>
              </Stack>
            )}
          </Typography>
        </Stack>
      }
    >
      {shareLinkVisible ? (
        <ShareLinkPage communityId={communityId} />
      ) : (
        <Stack minWidth='600px' padding={3} maxHeight='80vh' overflow='scroll'>
          <Box>
            <Tabs
              variant='fullWidth'
              disableRipple
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              aria-label='basic tabs example'
              sx={{
                background: 'var(--color-base)',
                padding: '5px',

                '& .MuiTabs-flexContainer': {
                  position: 'relative',
                  zIndex: 2,
                },
                '& .MuiButtonBase-root': {
                  textTransform: 'none',
                  color: 'var(--color-subtext)',
                },
                '& .Mui-selected': {
                  color: 'var(--color-text)',
                  fontWeight: 'bold',
                },
              }}
              TabIndicatorProps={{
                style: {
                  height: '100%',
                  border: `1px solid var(--color-subtext)`,
                  borderRadius: '4px',
                  background: 'white',
                  zIndex: 1,
                },
              }}
            >
              <Tab disableRipple label={_(msg`Add Manually`)} />
              <Tab disableRipple label={_(msg`Auto Generate`)} />
            </Tabs>
          </Box>
          <Box overflow='scroll'>
            <CustomTabPanel value={activeTab} index={0}>
              <ManualAddTab
                onSuccess={(data) => {
                  locallyUpdateLoginAllowances(data);
                  setAddedMembers((addedMembers) => addedMembers.concat(data));
                }}
                communityId={communityId}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={1}>
              <AutoAddTab
                communityId={communityId}
                onSuccess={(data) => {
                  locallyUpdateLoginAllowances(data);
                  setAddedMembers((addedMembers) => addedMembers.concat(data));
                }}
              />
            </CustomTabPanel>
          </Box>
          <Grid mt={4} alignItems='center' container>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid mt={3} item xs={12}>
              <Stack alignItems='center' direction='row'>
                <Typography fontWeight='bold'>
                  <Trans>New Members added:</Trans>
                </Typography>
                <Typography
                  ml={1}
                  sx={{
                    color: 'var(--color-primary)',
                  }}
                >
                  {addedMembers.length}
                </Typography>

                {addedMembers.length > 0 && (
                  <>
                    <Box
                      sx={{
                        height: '24px',
                      }}
                      title='Download CSV'
                    >
                      <FileDownloadIcon
                        style={{
                          width: '1.2rem',
                          cursor: 'pointer',
                          color: 'var(--color-primary)',
                          marginLeft: '1rem',
                        }}
                        onClick={() => {
                          let csv = addedMembers
                            .map((user) => {
                              return `${user.user.username},${user.user.password},${user.user.groups?.length ? user.user.groups.join(',') : ''},${user.user.id}`;
                            })
                            .join('\n');

                          csv = `username,password,groups,userId\n${csv}`;
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'members.csv';
                          a.click();
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        height: '24px',
                      }}
                      title='Copy to Clipboard'
                    >
                      <ContentCopyIcon
                        style={{
                          width: '1.2rem',
                          cursor: 'pointer',
                          color: 'var(--color-primary)',
                          marginLeft: '0.5rem',
                        }}
                        onClick={() => {
                          let csv = addedMembers
                            .map((user) => {
                              return `${user.user.username},${user.user.password},${user.user.groups?.length ? user.user.groups.join(',') : ''},${user.user.id}`;
                            })
                            .join('\n');

                          csv = `username,password,groups,userId\n${csv}`;
                          navigator.clipboard.writeText(csv);
                        }}
                      />
                    </Box>
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Stack
                direction='row'
                alignItems='center'
                gap='0.5rem'
                mb={addedMembers.length > 0 ? 3 : 0}
                flexWrap='wrap'
              >
                <ScrollableContainer
                  elements={addedMembers.map((user) => (
                    <Chip
                      key={user.id}
                      label={qualifiedUserName(user.user)}
                      size='small'
                      sx={{
                        backgroundColor: 'var(--color-selected)',
                        border: '1px solid var(--color-border)',
                      }}
                      onDelete={() => {
                        bulkDeleteUsers({
                          userIds: [user.user.id],
                        });
                        setAddedMembers((addedMembers) =>
                          addedMembers.filter((u) => u.id !== user.id),
                        );
                      }}
                    />
                  ))}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction='row' justifyContent='flex-end'>
                <Button
                  onClick={() => {
                    setShareLinkVisible(true);
                  }}
                  variant='contained'
                >
                  <Trans>Next: Share Link</Trans>
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      )}
    </CocoModal>
  );
}
